import { memo } from 'react';
import { DialogTrigger } from 'react-aria-components';

import { Button, CopyText, Flex, Heading, Label, Text } from '@/components/core';
import { AddIcon } from '@/components/icons';
import { InviteMembersModal, MembersTable } from '@/components/ui';
import { ManagedRole } from '@/constants';
import { useDots, useGetAppClientWithSecret } from '@/hooks';

import { Page } from './Page';
import { PersonalData } from './PersonalData.tsx';
import { Section, SectionCard } from './Section.tsx';
import { WorkspaceDetails } from './WorkspaceDetails.tsx';
import { WorkspaceHeader } from './WorkspaceHeader.tsx';

export type WorkspaceProps = object;

// eslint-disable-next-line no-empty-pattern
const IWorkspace = ({}: WorkspaceProps) => {
  const { data: appClient } = useGetAppClientWithSecret();
  const dots = useDots();

  return (
    <Page className="gap-md">
      <WorkspaceHeader>
        <Heading>Workspace</Heading>
        <Text>Manage your workspace.</Text>
      </WorkspaceHeader>

      <WorkspaceDetails showWorkspaceId className="border border-gray-200" />

      <Section title="Members" description="Manage your team and change roles and permissions.">
        <Flex col className="gap-sm">
          <MembersTable />
          <Flex className="w-full">
            <DialogTrigger>
              <Button variant="plain">
                <AddIcon />
                <Text>Add members</Text>
              </Button>
              <InviteMembersModal defaultRole={ManagedRole.Admin} />
            </DialogTrigger>
          </Flex>
        </Flex>
      </Section>

      <Section title="API" description="View your API credentials.">
        <SectionCard col>
          <Flex col className="gap-xs">
            <Label>Client ID</Label>
            <CopyText value={appClient?.clientId ?? `Loading ${dots}`} />
          </Flex>

          <Flex col className="gap-xs">
            <Label>Client Secret</Label>
            <CopyText isSensitive value={appClient?.clientSecret ?? `Loading ${dots}`} />
          </Flex>
        </SectionCard>
      </Section>

      <PersonalData />
    </Page>
  );
};

export const Workspace = memo(IWorkspace) as typeof IWorkspace;
